import { ApolloClient, ObservableQuery } from "@apollo/client";

const IMAGES_MAX_MS = 1500;

const wait = (ms: number) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

export const nextFrame = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );

const refetchQueriesOnScreen = async (client: ApolloClient<object>) => {
  const { queries, results } = client.refetchQueries({ include: "active" });
  const isRefreshed = await Promise.all(
    results.map((result) =>
      Promise.resolve(result).then(
        () => true,
        () => false,
      ),
    ),
  );
  return queries.filter((_, index) => isRefreshed[index]);
};

const keepOnlyRefreshedDataInCache = (
  client: ApolloClient<object>,
  refreshedQueries: ObservableQuery[],
) => {
  const snapshots = refreshedQueries.map((query) => ({
    query: query.query,
    variables: query.variables,
    data: query.getCurrentResult().data,
  }));
  client.cache.batch({
    optimistic: false,
    update: (cache) => {
      cache.evict({ id: "ROOT_QUERY" });
      cache.gc();
      snapshots.forEach(({ query, variables, data }) => {
        if (data) cache.writeQuery({ query, variables, data });
      });
    },
  });
};

const waitForImagesToDecode = async () => {
  await nextFrame();
  await nextFrame();
  await Promise.race([
    Promise.all(
      Array.from(document.images).map((image) =>
        image.decode().catch(() => undefined),
      ),
    ),
    wait(IMAGES_MAX_MS),
  ]);
};

export const refreshDataForNewProfile = async (
  client: ApolloClient<object>,
) => {
  const refreshedQueries = await refetchQueriesOnScreen(client);
  keepOnlyRefreshedDataInCache(client, refreshedQueries);
  await waitForImagesToDecode();
};
